import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import DropdownInput from "../../../components/DropdownInput";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import OutlineInput from "../../../components/OutlineInput";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex, nameRegEx} from "./WithdrawsForm";
import {Input} from "../../../types/Input";
import {WallettecBank, WallettecMethod} from "../../../types/Responses/WithdrawsResponse";
import CountryAvatar from "../../../components/CountryAvatar";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface WallettecFormProps {
    broker: number,
    clientTransactionID?: number
}

const Wallettec = ({broker, clientTransactionID}: WallettecFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [method, setMethod] = useState<number>();
    const [country, setCountry] = useState<number>();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [phone, setPhone] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [description, setDescription] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();
    const [accountOwner, setAccountOwner] = useState<string>();

    const [methods, setMethods] = useState<WallettecMethod[]>()
    const [banks, setBanks] = useState<WallettecBank[]>();
    const [bank, setBank] = useState<string>();
    const [branch, setBranch] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const bankCountries = ['Nigeria', 'South Africa'];

    const dispatch = useDispatch();
    const clearData = () => {
        setInProgress(false);
        setMethod(0);
        setAmount(0);
        setFirstName(undefined);
        setLastName(undefined);
        setEmail(undefined);
        setDescription(undefined);
        setAccountOwner(undefined);
        setAccountNumber(undefined);
        setValidated(false);
        setBank(undefined);
        setBranch(undefined);
        setCountry(undefined);
    }

    const getClientTransaction = useCallback(() => {
        if (!methods)
            return;
        console.log('getClientTransaction');
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.client.first_name);
            setLastName(response.data.client.last_name);
            setEmail(response.data.client.email);

            const tempCountry = methods.find(m => m.country.id === response.data.country.id);
            console.log(tempCountry);
            if (tempCountry) {
                setCountry(tempCountry.country.id);
                setMethod(tempCountry.id);
            }
            if (response.data.request && response.data.request.method) {
                setMethod(response.data.request.method.toLowerCase());
            }
            if (response.data.transaction_data) {
                setDescription(response.data.transaction_data.accountnumber)
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`, toast, handleError));
    }, [toast, handleError, methods]);

    const getMethods = useCallback(() => {
        return new Promise((resolve, reject) =>{
            API.getWallettecMethods(broker, 'wallettec').then((response: AxiosResponse) => {
                setMethods(response.data.data);
                resolve(undefined);
            }).catch(e => {
                ErrorHandler(e, `get${APILink.GET_WALLETTEC_METHODS}`, toast, handleError);
                reject(undefined);
            })
        });
    }, []);

    const getBanks = useCallback((ph: string) => {
        if (!method)
            return;
        API.getWallettecBanks(ph, method).then((response: AxiosResponse) => {
            setBanks(response.data);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WALLETTEC_BANKS}`, toast, handleError));
    }, [toast, handleError, method]);


    const changeMethod = useCallback((s: string | number) => {
        clearData();
        const v = typeof s !== "string" ? s : parseInt(s);
        setMethod(v);
        if (methods) {
            const selectedMethod = methods.find(m => m.id === v);
            if (selectedMethod) {
                setCountry(selectedMethod.country.id);
            }
        }
    }, [setMethod, clearData, methods]);

    const changeBank = useCallback((s: string | number) => {
        const v = typeof s !== "string" ? s.toString() : s;
        setBank(v);
        if (banks) {
            const selectedBank = banks.find(b => b.branchCode === v);
            if (selectedBank) {
                setBranch(v);
                setBank(selectedBank.bankName);
            }
        }
    }, [setBank, banks]);

    const changePhone = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (!method || !methods)
            return;
        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo)
            return;
        if (e?.currentTarget) {
            setValidated(false);
            setPhone(e.currentTarget.value)
        }
    }, [setValidated, setPhone, getBanks, method, methods]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const changeAccountOwner = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountOwner(e.currentTarget.value)
        }
    }, [setValidated, setAccountOwner]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeDescription = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setDescription(e.currentTarget.value)
        }
    }, [setValidated, setDescription]);

    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = 'Approve from broker is required'
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> Withdraw request created.<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            const request = {
                amount,
                method,
                email,
                broker,
                provider: WithdrawProvider.WALLETTEC,
                description,
                first_name: firstName,
                last_name: lastName,
                account_number: accountNumber,
                account_owner: accountOwner,
                phone,
                country,
                bank,
                branch
            };
            API.makeWallettecWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    ErrorHandler(e, `post${APILink.MAKE_WALLETTEC_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [amount, method, email, broker, description, firstName, lastName, accountNumber, accountOwner, phone, country, bank, branch, handleSuccessResponse, toast, handleError]);

    const submit = useCallback(() => {
        setInProgress(true);
        if (!methods)
            return;
        const inputs: Input[] = [
            {
                key: 'method',
                label: t('admin.withdraws.country'),
                type: InputType.select,
                options: methods.map((i) => {
                    return {name: i.country.name, id: i.country.id}
                })
            },
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
            {key: 'first_name', label: t('admin.withdraws.fist_name'), type: InputType.number, regex: nameRegEx},
            {key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.number, regex: nameRegEx},
            {key: 'email', label: t('admin.withdraws.email'), type: InputType.email, regex: emailRegex},
            {key: 'description', label: t('admin.withdraws.description'), type: InputType.text, regex: '^.{5,}'},
            {key: 'phone', label: t('admin.withdraws.phone'), type: InputType.text, regex: '^.{5,}'},
        ];
        const data = {
            method,
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            description,
            phone
        };

        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [methods, t, method, amount, firstName, lastName, email, description, phone, dispatch, callback]);

    const accountNumberUI = useMemo(() => {
        if (!banks || !method || !methods)
            return <></>
        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || bankCountries.includes(methodInfo.country.name))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [banks, method, methods, bankCountries, t, validated, accountNumber, changeAccountNumber])

    const accountOwnerUI = useMemo(() => {
        if (!banks || !method || !methods)
            return <></>
        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || !bankCountries.includes(methodInfo.country.name))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_owner')} type="text" validated={validated}
                          value={accountOwner}
                          fieldName="account_owner"
                          onChange={changeAccountOwner}/>
        </div>
    }, [banks, description, validated, accountOwner, changeAccountOwner])

    const bankUI = useMemo(() => {
        if (!banks || !method || !methods || !phone)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || !bankCountries.includes(methodInfo.country.name))
            return <></>

        return <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.bank')} className="w-full min-w-[200px]" selected={bank}
                           onChange={changeBank}
                           list={banks ? banks.map((i) => {
                               return {
                                   code: i.branchCode,
                                   name: i.bankName,
                               }
                           }) : []}/>
        </div>
    }, [banks, method, bank, methods, phone, changeBank])

    const amountUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || (bankCountries.includes(methodInfo.country.name) && !bank))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: methodInfo.currency})} type="number"
                          validated={validated}
                          value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [validated, method, methods, changeAmount, amount]);

    const phoneUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo)
            return <></>

        return <>
            <div className="max-w-md mt-4">
                <OutlineInput name={t('admin.withdraws.phone')} type="text" validated={validated} value={phone}
                              fieldName="phone"
                              onChange={changePhone}/>
            </div>
            {bankCountries.includes(methodInfo.country.name) && !banks ?
                <div className="max-w-md mt-4">
                    <Button className="bg-teal-500"
                            onClick={() => getBanks(phone ?? '')}>{t('admin.withdraws.continue')}</Button>
                </div> : ''}
        </>
    }, [method, methods, validated, phone, changePhone, banks])

    const firstNameUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || (bankCountries.includes(methodInfo.country.name) && !bank))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [method, methods, bankCountries, validated, firstName, changeFirstName])

    const lastNameUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || (bankCountries.includes(methodInfo.country.name) && !bank))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [method, methods, bank, validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || (bankCountries.includes(methodInfo.country.name) && !bank))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [method, methods, bank, validated, email, changeEmail])

    const descriptionUI = useMemo(() => {
        if (!method || !methods)
            return <></>

        const methodInfo = methods.find(i => i.id === method);
        if (!methodInfo || (bankCountries.includes(methodInfo.country.name) && !bank))
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.description')} type="text" validated={validated} value={description}
                          fieldName="description"
                          onChange={changeDescription}/>
        </div>
    }, [method, bank, methods, validated, description, changeDescription])

    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        clearData();
        getMethods().then(() => {
            if (clientTransactionID) {
                getClientTransaction();
            }
        })
    }, [broker, clientTransactionID, getMethods, getClientTransaction]);

    return <>
        <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.country')} className="w-full min-w-[200px]"
                           selected={method}
                           onChange={changeMethod}
                           list={methods ? methods.map((i) => {
                               return {
                                   id: i.id,
                                   name: i.country.name,
                                   icon: <CountryAvatar country={i.country} withName={false}/>
                               }
                           }) : []}/>
        </div>
        {phoneUI}
        {bankUI}
        {accountNumberUI}
        {accountOwnerUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {descriptionUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(Wallettec);
